'use client';

import React, { useState, useEffect } from 'react';
import { StarFilled } from '@ant-design/icons';

interface ReviewTeaserProps {
  entityId: number;
}

/**
 * Compact review teaser displayed below the hero images.
 * Listens for a 'review-summary-loaded' DOM event dispatched by
 * ReviewsSection once it has fetched the summary data.
 * Clicking scrolls smoothly to the full reviews section.
 */
const ReviewTeaser: React.FC<ReviewTeaserProps> = ({ entityId }) => {
  const [averageRating, setAverageRating] = useState<number | null>(null);
  const [totalReviews, setTotalReviews] = useState<number>(0);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) {
        setAverageRating(detail.average_rating);
        setTotalReviews(detail.total_reviews);
      }
    };
    window.addEventListener('review-summary-loaded', handler);
    return () => window.removeEventListener('review-summary-loaded', handler);
  }, []);

  if (averageRating === null || totalReviews === 0) {
    return null;
  }

  const scrollToReviews = () => {
    const el = document.getElementById('reviews-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <button
      onClick={scrollToReviews}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900 hover:underline focus:outline-none cursor-pointer"
    >
      <StarFilled style={{ color: '#fadb14', fontSize: 14 }} />
      <span className="font-bold">{averageRating.toFixed(2)}</span>
      <span className="text-gray-400 mx-0.5">·</span>
      <span className="underline">
        {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
      </span>
    </button>
  );
};

export default ReviewTeaser;
